import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import cx from "classnames";
import { useUnit } from "effector-react";

import { coordY, Touch, TouchGestureEvent } from "../Touch";
import {
  useCameraViewOffset,
  type ChatPanelMode,
  HUD_INDENT,
  CHAT_INPUT_HEIGHT,
  PANEL_SUGGESTIONS_GAP,
  SUGGESTIONS_HEIGHT,
} from "../../context/CameraViewOffsetContext";
import { $safeAreaInsets } from "../../state/safeArea";
import styles from "./AssistantChatPanel.module.css";

const modeClasses = {
  hidden: styles.hidden,
  // "third-screen": styles.modeThirdScreen,
  "half-screen": styles.modeHalfScreen,
  "full-screen": styles.modeFullScreen,
} as const;

const modeHeightExpression = {
  hidden: `var(--safe-bottom) + var(--chatInputHeight) + 2 * var(--hud-indent)`,
  "half-screen": `(var(--vvh) * 100 - var(--safe-top) - var(--safe-bottom)) * 0.5 + var(--safe-bottom)`,
  "full-screen": `(var(--vvh) * 100 - var(--safe-top)) - var(--hud-indent)`,
};

const FLICK_VELOCITY_THRESHOLD = 0.35; // px per ms

export type AssistantChatMode = ChatPanelMode;

type AssistantChatPanelProps = {
  children: ReactNode;
  footer: ReactNode;
  onModeChange?: (mode: AssistantChatMode) => void;
  onGestureChange?: (isSliding: boolean) => void;
  requestedMode?: AssistantChatMode | null;
  style?: CSSProperties;
};

export function AssistantChatPanel({
  children,
  footer,
  onModeChange,
  onGestureChange,
  requestedMode,
  style: externalStyle,
}: AssistantChatPanelProps) {
  const [mode, setMode] = useState<AssistantChatMode>("hidden");
  const [shift, setShift] = useState(0);
  const [isSliding, setIsSliding] = useState(false);
  const [duration, setDuration] = useState(600);
  const modeRef = useRef(mode);
  const shiftRef = useRef(shift);

  const { applyCurtainOffset, getCurtainOffset, viewport } = useCameraViewOffset();
  const safeArea = useUnit($safeAreaInsets);

  useEffect(() => {
    onModeChange?.(mode);
  }, [mode, onModeChange]);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    shiftRef.current = shift;
  }, [shift]);

  useEffect(() => {
    applyCurtainOffset(
      getCurtainOffset(modeRef.current, shiftRef.current),
      false
    );
  }, [applyCurtainOffset, getCurtainOffset, safeArea]);

  useEffect(() => {
    onGestureChange && onGestureChange(isSliding);
  }, [isSliding]);

  useEffect(() => {
    if (!requestedMode || requestedMode === modeRef.current) {
      return;
    }
    const nextDuration = 300;
    setMode(requestedMode);
    setDuration(nextDuration);
    setShift(0);
    setIsSliding(false);
    applyCurtainOffset(
      getCurtainOffset(requestedMode, 0),
      true,
      nextDuration
    );
  }, [applyCurtainOffset, getCurtainOffset, requestedMode]);

  const onMove = useCallback(
    (event: TouchGestureEvent) => {
      if (event.isSlideY && typeof event.shiftY === "number") {
        const nextShift = event.shiftY;
        setShift(nextShift);
        setIsSliding(true);
        applyCurtainOffset(
          getCurtainOffset(modeRef.current, nextShift),
          false
        );
      }
    },
    [applyCurtainOffset, getCurtainOffset]
  );

  const onEnd = useCallback(
    (event: TouchGestureEvent) => {
      const gestureDuration = Math.max(
        1,
        Math.floor(Number(new Date()) - Number(event.startT))
      );
      const shiftY = event.shiftY ?? 0;
      const velocityY = shiftY / gestureDuration;
      const isFling = Math.abs(velocityY) > FLICK_VELOCITY_THRESHOLD;

      const currentY = coordY(event.originalEvent) || shift;
      const y = isFling ? currentY + 0.5 * duration * velocityY : currentY;
      let nextMode: AssistantChatMode;

      switch (true) {
        case y < 0.25 * viewport.height:
          nextMode = "full-screen";
          break;

        case y >= 0.25 * viewport.height && y < 0.75 * viewport.height:
          nextMode = "half-screen";
          break;

        // case y >= 0.58 * viewport.height && y < 0.83 * viewport.height:
        //   nextMode = "third-screen";
        //   break;

        default:
          nextMode = "hidden";
          break;
      }

      setMode(nextMode);
      const nextDuration = Math.max(100, Math.min(600, gestureDuration));
      setDuration(nextDuration);
      applyCurtainOffset(
        getCurtainOffset(nextMode, 0),
        true,
        nextDuration
      );
      setShift(0);
      setIsSliding(false);
    },
    [applyCurtainOffset, getCurtainOffset, duration, shift, viewport.height]
  );

  const onFooterEnd = useCallback(
    (event: TouchGestureEvent) => {
      if (event.isSlideY) {
        onEnd(event);
      }
    },
    [onEnd]
  );

  useEffect(() => {
    const root: HTMLElement | null = document.querySelector('#app')

    if (!root) {
      return
    }

    let base = 0;

    switch (mode) {
      case "hidden":
        base = safeArea.bottom + CHAT_INPUT_HEIGHT + 2 * HUD_INDENT;
        break;
      // case "third-screen":
      //   base = 0.33 * viewport.height;
      //   break;
      case "half-screen":
        base = 0.5 * (viewport.height - safeArea.top);
        break;
      case "full-screen":
        base = viewport.height - safeArea.top;
        break;
    }

    const cur = base - shift;

    // Internal suggestions
    const imin = safeArea.bottom + CHAT_INPUT_HEIGHT + 2 * HUD_INDENT;
    const imax = imin + SUGGESTIONS_HEIGHT;
    const ivalue = (cur - imin) / (imax - imin)

    // External suggestions
    const emin = safeArea.bottom + CHAT_INPUT_HEIGHT + 2 * HUD_INDENT
    const emax = emin + PANEL_SUGGESTIONS_GAP
    const evalue = 1 - (cur - emin) / (emax - emin)

    // Last message widget
    // const amin = 0.33 * viewport.height - 2 * PANEL_INDENT
    // const amax = amin + 40
    // const avalue = 1 - (cur - amin) / (amax - amin)

    // History
    // const bmin = 0.33 * viewport.height - 2 * PANEL_INDENT
    // const bmax = bmin + 40
    // const bvalue = (cur - bmin) / (bmax - bmin)

    root.style = `
      --internalSuggestionsOpacity: ${ivalue.toFixed(2)};
      --externalSuggestionsOpacity: ${evalue.toFixed(2)};
    `;

    // --lastMessageOpacity: ${avalue.toFixed(2)};
    // --historyOpacity: ${bvalue.toFixed(2)};

  }, [mode, viewport.height, shift]);

  return (
    <div
      className={cx(
        styles.assistantChatPanel,
        modeClasses[mode],
        shift !== 0 && styles.dragging
      )}
      style={
        {
          height: `calc(${modeHeightExpression[mode]} - ${shift}px)`,
          "--transitionDuration": `${duration}ms`,
          ...externalStyle,
        } as CSSProperties
      }
    >
      <Touch onMoveY={onMove} onEnd={onEnd}>
        <div className={styles.dragger} />
      </Touch>
      <div className={styles.wrapper}>{children}</div>

      <Touch onMoveY={onMove} onEnd={onFooterEnd}>
        {footer}
      </Touch>
    </div>
  );
}
